import { NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal, TrackByFunction } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { SnoballService } from '@app/v2/event/services/snoball.service'
import { EventApp } from '@app/v2/event/types/event-app.type'
import { SnoballConfiguration } from '@app/v2/event/types/snoball.type'
import { Persona } from '@app/v2/personas/types/persona.type'
import { GenericTableComponent } from '@app/v2/shared/components/generic-table/generic-table.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { GenericTableCellDefDirective } from '@app/v2/shared/directives/generic-table-cell-def.directive'
import { GenericTableColumnDirective } from '@app/v2/shared/directives/generic-table-column.directive'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { SnoballConfigFormModalComponent } from '../snoball-config-form-modal/snoball-config-form-modal.component'

@Component({
  selector: 'app-snoball-setup-modal',
  standalone: true,
  imports: [
    NgTemplateOutlet,
    ModalComponent,
    DeleteModalComponent,
    TranslateModule,
    NgxTippyModule,
    TruncationTooltipComponent,
    SnoballConfigFormModalComponent,
    GenericTableComponent,
    GenericTableColumnDirective,
    GenericTableCellDefDirective
  ],
  templateUrl: './snoball-setup-modal.component.html',
  styleUrl: './snoball-setup-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SnoballSetupModalComponent {
  private static readonly _tableHeaderHeightPx = 45
  private static readonly _tableRowHeightPx = 64
  public readonly eventApp = input<EventApp | null>(null)
  public readonly eventId = input<number | null>(null)
  public readonly personas = input<Persona[]>([])
  public readonly onCloseModal = output<void>()

  public readonly modalId = 'snoball-setup-modal'
  public readonly configFormModalId = 'snoball-config-form-modal'
  public readonly disconnectModalId = 'snoball-disconnect-modal'
  public readonly headerIcon = computed(() => this._snoballService.headerIconHtml(this.eventApp()))
  public readonly iconActionTippyProps = { theme: 'dark' as const }
  public readonly configurations = signal<SnoballConfiguration[]>([])
  public readonly configToDisconnect = signal<SnoballConfiguration | null>(null)
  public readonly configToEdit = signal<SnoballConfiguration | null>(null)
  public readonly statusSavingId = signal<string | null>(null)
  public readonly canManage = computed(() => this._workspaceRoleService.canEditEvent())
  public readonly availablePersonas = computed(() => {
    const editingId = this.configToEdit()?.id
    const reserved = new Set(
      this.configurations()
        .filter((config) => config.id !== editingId)
        .flatMap((config) => config.persona_ids)
    )
    return this.personas().filter((persona) => !reserved.has(persona.id))
  })
  public readonly disconnectConfirmMessage = computed(() => this._translate.instant('v2.eventApps.snoball.disconnectConfirm'))
  public readonly tableHeightPx = computed(() => SnoballSetupModalComponent._tableHeaderHeightPx + this.configurations().length * SnoballSetupModalComponent._tableRowHeightPx)
  public readonly trackByConfiguration: TrackByFunction<SnoballConfiguration> = (_index, config) => config.id

  /**
   * Stable row key for the configurations table.
   * @param {SnoballConfiguration} config - Configuration row.
   * @returns {string}
   */
  public configurationRowKey(config: SnoballConfiguration): string {
    return config.id
  }

  private readonly _snoballService = inject(SnoballService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _alertService = inject(AlertService)
  private readonly _modalService = inject(ModalService)
  private readonly _translate = inject(TranslateService)
  private readonly _destroyRef = inject(DestroyRef)

  constructor() {
    effect((onCleanup) => {
      const eventId = this.eventId()
      if (!eventId) {
        this.configurations.set([])
        return
      }
      const sub = this._snoballService.listConfigurations(eventId).subscribe({
        next: (configs) => this.configurations.set(configs),
        error: () => this.configurations.set([])
      })
      onCleanup(() => sub.unsubscribe())
    })
  }

  /**
   * Resolves persona names for a configuration row.
   * @param {SnoballConfiguration} config - Configuration to label.
   * @returns {string}
   */
  public personaLabels(config: SnoballConfiguration): string {
    const names = config.persona_ids.map((personaId) => this.personas().find((persona) => persona.id === personaId)?.name).filter((name): name is string => Boolean(name))
    return names.length ? names.join(', ') : config.persona_ids.join(', ')
  }

  /**
   * Opens the add-configuration form when at least one persona is still free.
   */
  public onAddConfiguration(): void {
    if (!this.canManage()) return
    this.configToEdit.set(null)
    if (this.availablePersonas().length === 0) {
      this._alertService.alert(Alert.WARNING, this._translate.instant('v2.eventApps.snoball.noAvailablePersonas'))
      return
    }
    this._openModal(this.configFormModalId)
  }

  /**
   * Opens the configuration form prefilled for an existing mapping.
   * @param {SnoballConfiguration} config - Configuration to edit.
   */
  public onEditRequested(config: SnoballConfiguration): void {
    if (!this.canManage()) return
    this.configToEdit.set(config)
    this._openModal(this.configFormModalId)
  }

  /**
   * Clears the pending edit target when the form modal closes.
   */
  public onConfigFormClosed(): void {
    this.configToEdit.set(null)
  }

  /**
   * Persists an active/inactive toggle for one configuration.
   * Not tied to this component's destroy ref: closing the modal must not abort the save.
   * @param {SnoballConfiguration} config - Configuration to update.
   * @param {Event} event - Change event from the status switch.
   */
  public onStatusToggled(config: SnoballConfiguration, event: Event): void {
    if (!this.canManage() || this.statusSavingId()) return
    const checked = (event.target as HTMLInputElement | null)?.checked ?? config.status === 'active'
    const status: SnoballConfiguration['status'] = checked ? 'active' : 'inactive'
    if (config.status === status) return

    const eventId = this.eventId()
    if (!eventId) return

    this.statusSavingId.set(config.id)
    this.configurations.update((list) => list.map((row) => (row.id === config.id ? { ...row, status } : row)))
    this._snoballService.toggleConfigurationStatus(eventId, config.id, status).subscribe({
      next: () => {
        this.statusSavingId.set(null)
        this._reloadConfigurations()
        const messageKey = status === 'active' ? 'v2.eventApps.snoball.statusActivated' : 'v2.eventApps.snoball.statusDeactivated'
        this._alertService.alert(Alert.SUCCESS, this._translate.instant(messageKey))
      },
      error: (err) => {
        this.statusSavingId.set(null)
        this._reloadConfigurations()
        this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? this._translate.instant('v2.eventApps.snoball.editError'))
      }
    })
  }

  /**
   * Opens the disconnect confirmation for one configuration.
   * @param {SnoballConfiguration} config - Configuration to disconnect.
   */
  public onDisconnectRequested(config: SnoballConfiguration): void {
    if (!this.canManage()) return
    this.configToDisconnect.set(config)
    this._openModal(this.disconnectModalId)
  }

  /**
   * Whether the add-configuration modal is open.
   * @returns {boolean}
   */
  public isConfigFormModalOpen(): boolean {
    return this._modalService.isModalOpen(this.configFormModalId)
  }

  /**
   * Whether the disconnect confirmation is open.
   * @returns {boolean}
   */
  public isDisconnectModalOpen(): boolean {
    return this._modalService.isModalOpen(this.disconnectModalId)
  }

  /**
   * Closes the disconnect confirmation and clears the pending target.
   * Delete-modal cancel does not close itself; the parent must toggle.
   */
  public closeDisconnectModal(): void {
    if (this._modalService.isModalOpen(this.disconnectModalId)) {
      this._modalService.toggleModal(this.disconnectModalId)
    }
    this.configToDisconnect.set(null)
  }

  /**
   * Removes the selected configuration without refunding the wallet.
   */
  public onDisconnectConfirmed(): void {
    const config = this.configToDisconnect()
    const eventId = this.eventId()
    this.configToDisconnect.set(null)
    if (!config || !eventId) return

    this._snoballService
      .disconnectConfiguration(eventId, config.id)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          this._reloadConfigurations()
          this._alertService.alert(Alert.SUCCESS, this._translate.instant('v2.eventApps.snoball.disconnectSuccess'))
        },
        error: (err) => {
          this._alertService.alert(Alert.ERROR, err?.error?.message ?? err?.message ?? this._translate.instant('v2.eventApps.snoball.disconnectError'))
        }
      })
  }

  /**
   * Refreshes configuration rows after the add/edit form saves.
   */
  public onConfigurationsSaved(): void {
    this._reloadConfigurations()
  }

  /**
   * Reloads configuration rows from persona form `options.snoball`.
   */
  private _reloadConfigurations(): void {
    const eventId = this.eventId()
    if (!eventId) return
    this._snoballService
      .listConfigurations(eventId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (configs) => this.configurations.set(configs),
        error: () => this.configurations.set([])
      })
  }

  /**
   * Opens a modal when it is not already visible.
   * @param {string} id - Modal id.
   */
  private _openModal(id: string): void {
    if (!this._modalService.isModalOpen(id)) this._modalService.toggleModal(id)
  }
}
